import type { ManifestFormVariant } from "components/Document/ReviewInteraction";

import { safeParseInt } from "encamp-shared/src/tierii/utilities";

/**
 * Which box of EPA Form 8700-22 each review-form field came off, so the document
 * pane can point at it while the reviewer types.
 *
 * The form already knows this — every label reads "11 · Total quantity" — but it
 * knew it as label text, which nothing else can read. This is the same knowledge
 * as data.
 *
 * Not `FIELD_SECTION` from `chFormat.ts`, which the design doc proposed
 * promoting. That map is keyed by the *discrepancy comparators'* field names
 * (`generatorStreetAddress`), not the review form's (`generatorLocationStreet1`)
 * — `reviewFieldDiffs` exists to bridge exactly those two namespaces — it covers
 * only the fifteen fields a comparator can emit where the form has around sixty,
 * and it has no notion of the continuation sheet. It keeps doing its own job for
 * discrepancy rows.
 */

/** Where a field sits on the printed form. */
export interface FieldBox {
  variant: ManifestFormVariant;
  /** The printed box number, e.g. "9b" or "11". */
  box: string;
  /** Which row of the waste-line table, for a box that repeats down it. */
  rowIndex?: number;
  /** What the pane header says, e.g. "Box 11 · Total quantity". */
  label: string;
}

/** EPA Form 8700-22 holds four waste lines; the fifth onward continue on 8700-22A. */
const MAIN_FORM_WASTE_LINE_COUNT = 4;
/** Boxes 6 and 7 hold the first two transporters; the rest continue on 8700-22A. */
const MAIN_FORM_TRANSPORTER_COUNT = 2;

/** Manifest-level fields, which all live on the main form. */
const MANIFEST_FIELD_BOX: Record<string, { box: string; label: string }> = {
  manifestTrackingNumber: { box: "4", label: "Manifest tracking number" },
  generatorId: { box: "1", label: "Generator EPA ID" },
  // Printed inside Box 1 after the EPA ID on Texas manifests, which is the whole
  // reason the field exists.
  generatorTxStateWasteRegistrationId: { box: "1", label: "TX state registration number" },
  generatorName: { box: "5", label: "Generator name" },
  generatorMailStreet1: { box: "5", label: "Generator mailing address" },
  generatorMailStreet2: { box: "5", label: "Generator mailing address" },
  generatorMailCity: { box: "5", label: "Generator mailing address" },
  generatorMailState: { box: "5", label: "Generator mailing address" },
  generatorMailZip: { box: "5", label: "Generator mailing address" },
  generatorSiteAddressSameAsMailing: { box: "5", label: "Generator site address" },
  generatorLocationStreet1: { box: "5", label: "Generator site address" },
  generatorLocationStreet2: { box: "5", label: "Generator site address" },
  generatorLocationCity: { box: "5", label: "Generator site address" },
  generatorLocationState: { box: "5", label: "Generator site address" },
  generatorLocationZip: { box: "5", label: "Generator site address" },
  desFacilityId: { box: "8", label: "Designated facility EPA ID" },
  desFacilityName: { box: "8", label: "Designated facility" },
  desFacLocationStreet1: { box: "8", label: "Designated facility address" },
  desFacLocationCity: { box: "8", label: "Designated facility address" },
  desFacLocationState: { box: "8", label: "Designated facility address" },
  desFacLocationZip: { box: "8", label: "Designated facility address" },
  shippedDate: { box: "15", label: "Date shipped" },
  receivedDate: { box: "20", label: "Date received" },
  discrepancyComments: { box: "18", label: "Discrepancy" },
  residueComments: { box: "18", label: "Residue" },
  rejectionComments: { box: "18", label: "Rejection" },
};

/**
 * A waste-line column, per sheet. The continuation sheet numbers the same
 * columns differently, and the management-method code sits outside the table on
 * both.
 */
const WASTE_LINE_BOX: Record<string, { main: string; continuation: string; label: string }> = {
  dotHazardous: { main: "9a", continuation: "27a", label: "DOT hazardous" },
  dotIdNumberDescription: {
    main: "9b",
    continuation: "27b",
    label: "DOT ID / description",
  },
  dotPrintedInformation: {
    main: "9b",
    continuation: "27b",
    label: "DOT printed information",
  },
  nonHazWasteDescription: {
    main: "9b",
    continuation: "27b",
    label: "Non-haz waste description",
  },
  containerNumber: { main: "10", continuation: "28", label: "No. of containers" },
  containerTypeCode: { main: "10", continuation: "28", label: "Container type" },
  quantity: { main: "11", continuation: "29", label: "Total quantity" },
  qtyUnitOfMeasureCode: { main: "12", continuation: "30", label: "Unit wt./vol." },
  federalWasteCodes: {
    main: "13",
    continuation: "31",
    label: "Federal waste codes",
  },
  stateWasteCodes: { main: "13", continuation: "31", label: "State waste codes" },
  // A row of numbered sub-boxes below the table, not a column of it.
  managementMethodCode: {
    main: "19",
    continuation: "36",
    label: "Management method code",
  },
};

/** Transporter fields share their transporter's box. */
const TRANSPORTER_FIELD_LABEL: Record<string, string> = {
  transporterId: "Transporter EPA ID",
  transporterName: "Transporter company name",
};

/** Matches a repeated field path, e.g. `wasteLines.2.quantity`. */
const ROW_FIELD = /^(wasteLines|transporters)\.(\d+)\.(.+)$/;

function labelled(box: string, label: string): string {
  return `Box ${box} · ${label}`;
}

/**
 * Where the field at this form path came off the printed form, or null when it
 * has no box — the facility association and the staff comments are ours, not the
 * form's, and pointing at a box for them would be a lie.
 */
export function manifestFieldBox(fieldPath: string): FieldBox | null {
  const row = ROW_FIELD.exec(fieldPath);

  if (row == null) {
    const manifestField = MANIFEST_FIELD_BOX[fieldPath];
    if (manifestField == null) return null;
    return {
      variant: "8700-22",
      box: manifestField.box,
      label: labelled(manifestField.box, manifestField.label),
    };
  }

  const [, group, indexText, field] = row;
  const index = safeParseInt(indexText);
  if (index == null) return null;

  if (group === "transporters") {
    const label = TRANSPORTER_FIELD_LABEL[field];
    if (label == null) return null;
    return transporterFieldBox(index, label);
  }

  const column = WASTE_LINE_BOX[field];
  if (column == null) return null;
  return wasteLineFieldBox(index, column);
}

/** Boxes 6 and 7, then 25 and 26 alternating down the continuation sheets. */
function transporterFieldBox(index: number, label: string): FieldBox {
  if (index < MAIN_FORM_TRANSPORTER_COUNT) {
    const box = String(index + 6);
    return { variant: "8700-22", box, label: labelled(box, `${label} ${index + 1}`) };
  }
  const box = (index - MAIN_FORM_TRANSPORTER_COUNT) % 2 === 0 ? "25" : "26";
  return { variant: "8700-22A", box, label: labelled(box, `${label} ${index + 1}`) };
}

/**
 * A waste line's box, and which of that box's repeats belongs to this line.
 *
 * Every waste-line box repeats per line — the table's columns down the table,
 * and the management method codes across their own grid below it — so the line
 * index goes to all of them and `boxRegion` knows which shape it is placing.
 */
function wasteLineFieldBox(index: number, column: (typeof WASTE_LINE_BOX)[string]): FieldBox {
  const onMainForm = index < MAIN_FORM_WASTE_LINE_COUNT;
  const box = onMainForm ? column.main : column.continuation;
  const rowIndex = onMainForm ? index : index - MAIN_FORM_WASTE_LINE_COUNT;

  return {
    variant: onMainForm ? "8700-22" : "8700-22A",
    box,
    rowIndex,
    label: labelled(box, `${column.label} (line ${index + 1})`),
  };
}
